// Sidebar pane that shows every Telegram chat for the active profile.
// Owns the WS subscription for the channels list, the "Refresh" trigger,
// and a search box. Selecting a chat lifts state up to WorkspacePage so
// the Trading Channels card on the right reflects only that chat.
import { useCallback, useEffect, useMemo, useState } from "react";
import type { TelegramChannelSummary } from "@shared/api/schemas/telegram";
import type {
	TradingChannelConfig,
	TradingChannelKind,
} from "@shared/api/schemas/trading-channels";
import { ApiError, httpClient } from "../../client/http-client";
import { wsClient } from "../../client/ws-client";
import { RefreshIcon } from "../../components/Icons";
import { ChannelList } from "./ChannelList";

interface ChatListPaneProps {
	profileId: string | null;
	channels: TelegramChannelSummary[];
	telegramReady: boolean;
	tradingChannels: TradingChannelConfig[];
	selectedChatId: number | null;
	onSelectChat: (chatId: number | null) => void;
}

const buildKindsMap = (
	configs: TradingChannelConfig[],
): Map<number, TradingChannelKind> => {
	const map = new Map<number, TradingChannelKind>();
	for (const c of configs) {
		if (c.chatId !== null) map.set(c.chatId, c.kind);
	}
	return map;
};

export const ChatListPane = ({
	profileId,
	channels,
	telegramReady,
	tradingChannels,
	selectedChatId,
	onSelectChat,
}: ChatListPaneProps) => {
	const [search, setSearch] = useState("");
	const [refreshing, setRefreshing] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const tradingKindsByChatId = useMemo(
		() => buildKindsMap(tradingChannels),
		[tradingChannels],
	);

	const filteredChannels = useMemo(() => {
		if (!search.trim()) return channels;
		const q = search.trim().toLowerCase();
		return channels.filter((c) => c.title.toLowerCase().includes(q));
	}, [channels, search]);

	const handleRefresh = useCallback(async () => {
		if (!profileId) return;
		setRefreshing(true);
		setError(null);
		try {
			await httpClient.telegram.refreshChannels(profileId);
		} catch (err) {
			setError(err instanceof ApiError ? err.message : String(err));
		} finally {
			setRefreshing(false);
		}
	}, [profileId]);

	// Keep the WS subscription warm so the channels list updates on backend
	// state changes (e.g. after refreshChannels server-side).
	useEffect(() => {
		if (!profileId) return;
		const unsub = wsClient.subscribeProfile(profileId);
		return unsub;
	}, [profileId]);

	return (
		<div className="grid h-full min-h-0 grid-rows-[auto_auto_auto_1fr] overflow-hidden">
			<div className="flex items-center justify-between gap-2 px-3 pb-2 pt-3">
				<span className="tg-text-muted text-[0.7rem] font-semibold uppercase tracking-[0.14em]">
					Telegram chats
				</span>
				<button
					type="button"
					onClick={() => void handleRefresh()}
					disabled={!telegramReady || refreshing || !profileId}
					className="tg-btn-icon tg-btn-secondary tg-text-secondary inline-flex h-7 w-7 rounded-lg disabled:opacity-55"
					aria-label="Refresh chats"
					title="Refresh chats"
				>
					<RefreshIcon className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
				</button>
			</div>

			<div className="px-3 pb-2">
				<input
					className="tg-input tg-text-primary h-9 w-full rounded-lg px-3 text-[0.85rem]"
					placeholder="Search chats"
					value={search}
					onChange={(event) => setSearch(event.target.value)}
					disabled={!telegramReady}
				/>
			</div>

			{error ? (
				<div className="tg-alert-error mx-3 mb-2 rounded-lg px-3 py-2 text-[0.8rem]" role="alert">
					{error}
				</div>
			) : (
				<div />
			)}

			<div className="min-h-0 overflow-hidden px-2 pb-2">
				<ChannelList
					channels={filteredChannels}
					onSelect={(chatId) =>
						onSelectChat(chatId === selectedChatId ? null : chatId)
					}
					selectedChatId={selectedChatId}
					isLoading={false}
					tradingKindsByChatId={tradingKindsByChatId}
					emptyTitle={
						telegramReady ? "No chats found" : "Telegram not connected"
					}
					emptyDescription={
						telegramReady
							? "Click refresh to reload the chat list from Telegram."
							: "Complete the Telegram login on the right to load your chats."
					}
				/>
			</div>
		</div>
	);
};
